import { UserInfo } from '../types'
import type { AppStore } from '../store'
import { jwtDecode } from './jwt-helper'
interface DecodedToken {
  exp: number
  [key: string]: any
}

// Safe wrapper — localStorage is blocked in some contexts (Safari ITP, Firefox
// strict mode, incognito, embedded iframes). All operations silently no-op on
// SecurityError rather than crashing the app.
export const safeLocalStorage = {
  getItem: (key: string): string | null => {
    try {
      return localStorage.getItem(key)
    } catch {
      return null
    }
  },
  setItem: (key: string, value: string): void => {
    try {
      localStorage.setItem(key, value)
    } catch {
      // persistence unavailable — state still lives in Redux
    }
  },
  removeItem: (key: string): void => {
    try {
      localStorage.removeItem(key)
    } catch {
      // ignore
    }
  },
  clear: (): void => {
    try {
      localStorage.clear()
    } catch {
      // ignore
    }
  },
}

let _store: AppStore | undefined

export const injectStore = (s: AppStore): void => {
  _store = s
}

export const getAccessToken = (): string => {
  return (
    _store?.getState().auth.accessToken ??
    safeLocalStorage.getItem('access-token') ??
    ''
  )
}

export const setAccessTokenToLocal = (token: string): void => {
  safeLocalStorage.setItem('access-token', token)
}

// Bridge so the module-level axios interceptors can trigger an Auth0 token refresh, which is only
// available from React context (getAccessTokenSilently). Registered once from <App/>, mirroring the
// injectStore pattern above.
type TokenRefresher = (forceRefresh: boolean) => Promise<string | undefined>
let _tokenRefresher: TokenRefresher | undefined

export const injectTokenRefresher = (fn: TokenRefresher): void => {
  _tokenRefresher = fn
}

// Mints a fresh access token via the injected Auth0 refresher (refresh-token grant on mobile,
// silent auth on web) and stores it. Returns undefined if no refresher is registered yet or the
// refresh fails (e.g. the refresh token itself is expired/revoked) — callers then surface the 401.
export const refreshAccessToken = async (
  forceRefresh = true,
): Promise<string | undefined> => {
  if (!_tokenRefresher) {
    return undefined
  }
  try {
    const token = await _tokenRefresher(forceRefresh)
    if (token) {
      // A successful mint means the session is healthy again — reopen the auth-failure window so a
      // later, independent expiry can trigger a fresh re-login instead of being silently debounced.
      resetAuthFailure()
    }
    return token
  } catch {
    return undefined
  }
}

// Bridge so the module-level axios interceptors can kick off an Auth0 re-login when the refresh
// token itself is dead (expired/revoked) — i.e. refreshAccessToken can no longer recover. Mirrors
// injectTokenRefresher: the actual loginWithRedirect lives in React context and is registered once
// from <App/>. No handler registered → no-op.
type AuthFailureHandler = () => void
let _authFailureHandler: AuthFailureHandler | undefined

// When a session dies, many in-flight requests 401 at once; each would otherwise call
// triggerAuthFailure. Debounce so the re-login fires at most once per expiry window. The window
// closes after AUTH_FAILURE_DEBOUNCE_MS or when a fresh token is minted (resetAuthFailure).
const AUTH_FAILURE_DEBOUNCE_MS = 30_000
let _authFailureFiredAt = 0

export const injectAuthFailureHandler = (fn: AuthFailureHandler): void => {
  _authFailureHandler = fn
}

// Reopen the auth-failure window so the next dead-token event can re-trigger a login.
export const resetAuthFailure = (): void => {
  _authFailureFiredAt = 0
}

// Fire the registered re-login handler at most once per expiry window. Safe to call from every
// 401 handler — repeated calls within the debounce window are ignored.
export const triggerAuthFailure = (): void => {
  if (!_authFailureHandler) {
    return
  }
  const now = Date.now()
  if (_authFailureFiredAt && now - _authFailureFiredAt < AUTH_FAILURE_DEBOUNCE_MS) {
    return
  }
  _authFailureFiredAt = now
  _authFailureHandler()
}

export const getUserInfoLocalStorage = (): UserInfo => {
  return JSON.parse(safeLocalStorage.getItem('userInfo') ?? '{}')
}

export const clearLocalStorage = (): void => {
  safeLocalStorage.removeItem('login')
  safeLocalStorage.removeItem('userInfo')
  safeLocalStorage.removeItem('access-token')
  safeLocalStorage.removeItem('location')
}

export const checkAuthToken = (): boolean => {
  try {
    const token = getAccessToken()

    if (!token) {
      return false
    }

    const decodedToken: DecodedToken = jwtDecode(token)
    const currentTime = Math.floor(Date.now() / 1000)

    return decodedToken.exp > currentTime
  } catch (error) {
    return false
  }
}

export const checkAndClearAuth = (): boolean => {
  if (!checkAuthToken()) {
    clearLocalStorage()
  }
  return checkAuthToken()
}
